import { z } from 'zod';

/* ------------------------------------------------------------------ */
/* Tid                                                                 */
/* ------------------------------------------------------------------ */

/**
 * All tid inom en dag är minuter från midnatt, lokal svensk tid.
 * Timvyn ritas mellan DAY_START_MIN och DAY_END_MIN med 1px per minut.
 * Se docs/SPEC.md § Tidsmodell innan du ändrar något här.
 */
export const DAY_START_MIN = 8 * 60;
export const DAY_END_MIN = 16 * 60;
export const PX_PER_MIN = 1;

export function minutesToHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function hhmmToMinutes(value: string): number {
  const [h, m] = value.split(':');
  return Number(h) * 60 + Number(m ?? 0);
}

/** "08:20–09:30" */
export function formatRange(startMin: number, endMin: number): string {
  return `${minutesToHHMM(startMin)}–${minutesToHHMM(endMin)}`;
}

/** "2 h 35 min", "45 min", "nu" */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return 'nu';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/* ------------------------------------------------------------------ */
/* Värdeförråd                                                         */
/* ------------------------------------------------------------------ */

export const COLOR_KEYS = [
  'math',
  'swedish',
  'english',
  'social',
  'science',
  'sports',
  'craft',
  'break',
] as const;
export type ColorKey = (typeof COLOR_KEYS)[number];
export const colorKeySchema = z.enum(COLOR_KEYS);

export const LESSON_KINDS = ['lesson', 'break', 'lunch'] as const;
export type LessonKind = (typeof LESSON_KINDS)[number];

export const LESSON_STATUSES = ['empty', 'draft', 'published'] as const;
export type LessonStatus = (typeof LESSON_STATUSES)[number];

export const SUBMIT_MODES = ['none', 'in_lesson', 'classroom', 'upload'] as const;
export type SubmitMode = (typeof SUBMIT_MODES)[number];

export const SUBMIT_MODE_LABELS: Record<SubmitMode, string> = {
  none: 'Inget att lämna in',
  in_lesson: 'Visas för läraren på lektionen',
  classroom: 'Lämnas in i Classroom',
  upload: 'Laddas upp här',
};

export const CHAT_MODES = ['hints', 'free'] as const;
export type ChatMode = (typeof CHAT_MODES)[number];

export const MEMBER_ROLES = ['student', 'teacher'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export type ViewerRole = MemberRole | 'guardian';

/* ------------------------------------------------------------------ */
/* Modeller som går över nätet                                         */
/* ------------------------------------------------------------------ */

export interface SubjectRef {
  id: string;
  name: string;
  colorKey: ColorKey;
}

export interface ScheduleLesson {
  id: string;
  kind: LessonKind;
  subject: SubjectRef | null;
  startMin: number;
  endMin: number;
  room: string | null;
  title: string | null;
  status: LessonStatus;
  hasContent: boolean;
  assignmentCount: number;
  hasExam: boolean;
  chatEnabled: boolean;
}

export interface DayView {
  date: string;
  weekday: number;
  className: string;
  dayStartMin: number;
  dayEndMin: number;
  lessons: ScheduleLesson[];
}

export interface WeekDay {
  date: string;
  weekday: number;
  lessons: ScheduleLesson[];
}

export interface UpcomingItem {
  assignmentId: string;
  title: string;
  dueDate: string | null;
  isExam: boolean;
  colorKey: ColorKey;
}

export interface WeekView {
  week: string;
  isoWeek: number;
  className: string;
  days: WeekDay[];
  upcoming: UpcomingItem[];
}

export interface LessonStep {
  id: string;
  position: number;
  text: string;
}

export interface LessonHelp {
  id: string;
  position: number;
  text: string;
}

export interface LessonLink {
  id: string;
  position: number;
  title: string;
  url: string;
  sourceLabel: string | null;
}

export interface LessonBook {
  title: string | null;
  pages: string | null;
  exercises: string | null;
}

export interface LessonAssignment {
  id: string;
  title: string;
  dueDate: string | null;
  dueMin: number | null;
  isExam: boolean;
  state: TaskState;
}

export interface NextLessonHint {
  id: string;
  subjectName: string;
  startMin: number;
  room: string | null;
  bringNote: string | null;
}

export interface LessonDetail {
  id: string;
  date: string;
  startMin: number;
  endMin: number;
  room: string | null;
  subject: SubjectRef | null;
  title: string | null;
  steps: LessonStep[];
  helps: LessonHelp[];
  links: LessonLink[];
  book: LessonBook;
  handInNote: string | null;
  submitMode: SubmitMode;
  status: LessonStatus;
  visibleFrom: string | null;
  chatEnabled: boolean;
  chatMode: ChatMode;
  assignments: LessonAssignment[];
  next: NextLessonHint | null;
}

export const TASK_STATES = ['todo', 'done'] as const;
export type TaskState = (typeof TASK_STATES)[number];

export type TaskSource = 'assignment' | 'personal';

export interface TaskItem {
  id: string;
  source: TaskSource;
  title: string;
  subtitle: string | null;
  colorKey: ColorKey | null;
  lessonId: string | null;
  dueDate: string | null;
  dueMin: number | null;
  isExam: boolean;
  state: TaskState;
  canDelete: boolean;
}

export interface TaskGroup {
  key: string;
  label: string;
  items: TaskItem[];
}

export interface SubjectProgress {
  subjectId: string;
  name: string;
  colorKey: ColorKey;
  done: number;
  total: number;
}

export interface TaskListView {
  groups: TaskGroup[];
  progress: {
    done: number;
    total: number;
    bySubject: SubjectProgress[];
  };
}

export interface TemplatePayload {
  title: string;
  steps: string[];
  helps: string[];
  links: { title: string; url: string; sourceLabel?: string | null }[];
  bookTitle: string;
  bookPages: string;
  bookExercises: string;
  submitMode: SubmitMode;
  handInNote: string;
  chatEnabled: boolean;
  chatMode: ChatMode;
}

export interface Template {
  id: string;
  classId: string;
  subjectId: string | null;
  name: string;
  description: string;
  payload: TemplatePayload;
}

export interface ChatMessage {
  id: string;
  role: 'student' | 'assistant';
  content: string;
  createdAt: number;
}

export interface ChatThreadView {
  threadId: string | null;
  enabled: boolean;
  mode: ChatMode;
  messages: ChatMessage[];
  suggestions: string[];
}

export interface Membership {
  classId: string;
  className: string;
  role: ViewerRole;
}

export interface MeView {
  user: {
    id: string;
    name: string;
    email: string;
    pictureUrl: string | null;
  };
  memberships: Membership[];
  guardianOf: { userId: string; name: string }[];
  activeClassId: string | null;
}

/* ------------------------------------------------------------------ */
/* Scheman för det som skrivs                                          */
/* ------------------------------------------------------------------ */

const urlSchema = z
  .string()
  .trim()
  .url('Länken måste börja med http:// eller https://')
  .refine((v) => v.startsWith('http://') || v.startsWith('https://'), {
    message: 'Bara http och https är tillåtna.',
  });

export const lessonLinkInputSchema = z.object({
  title: z.string().trim().min(1, 'Länken behöver en titel').max(120),
  url: urlSchema,
  sourceLabel: z.string().trim().max(60).nullish(),
});

export const lessonUpdateSchema = z.object({
  title: z.string().trim().max(160).default(''),
  steps: z.array(z.string().trim().min(1).max(400)).max(12).default([]),
  helps: z.array(z.string().trim().min(1).max(160)).max(12).default([]),
  links: z.array(lessonLinkInputSchema).max(12).default([]),
  book: z
    .object({
      title: z.string().trim().max(120).default(''),
      pages: z.string().trim().max(60).default(''),
      exercises: z.string().trim().max(120).default(''),
    })
    .default({ title: '', pages: '', exercises: '' }),
  handInNote: z.string().trim().max(240).default(''),
  submitMode: z.enum(SUBMIT_MODES).default('none'),
  visibleFrom: z.string().datetime({ offset: true }).nullable().default(null),
  chatEnabled: z.boolean().default(true),
  chatMode: z.enum(CHAT_MODES).default('hints'),
  status: z.enum(LESSON_STATUSES).default('draft'),
})
  .superRefine((value, ctx) => {
    // En publicerad lektion utan steg är en tom sida för eleven — den ska
    // gå att spara som utkast, men inte publiceras. Regeln ligger i schemat
    // och inte i formuläret, så att den gäller även för anrop utanför appen.
    if (value.status === 'published' && value.steps.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps'],
        message: 'Lägg till minst ett steg innan du publicerar.',
      });
    }
  });
export type LessonUpdateInput = z.infer<typeof lessonUpdateSchema>;

export const templatePayloadSchema = z.object({
  title: z.string().trim().max(160).default(''),
  steps: z.array(z.string().trim().min(1).max(400)).max(12).default([]),
  helps: z.array(z.string().trim().min(1).max(160)).max(12).default([]),
  links: z.array(lessonLinkInputSchema).max(12).default([]),
  bookTitle: z.string().trim().max(120).default(''),
  bookPages: z.string().trim().max(60).default(''),
  bookExercises: z.string().trim().max(120).default(''),
  submitMode: z.enum(SUBMIT_MODES).default('none'),
  handInNote: z.string().trim().max(240).default(''),
  chatEnabled: z.boolean().default(true),
  chatMode: z.enum(CHAT_MODES).default('hints'),
});

export const templateInputSchema = z.object({
  name: z.string().trim().min(1, 'Mallen behöver ett namn').max(80),
  description: z.string().trim().max(200).default(''),
  subjectId: z.string().nullable().default(null),
  payload: templatePayloadSchema,
});

export const applyTemplateSchema = z.object({
  templateId: z.string().min(1),
  overwrite: z.boolean().default(false),
  commit: z.boolean().default(false),
});

export const personalTaskSchema = z.object({
  title: z.string().trim().min(1, 'Skriv vad du ska göra').max(160),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum ska vara YYYY-MM-DD')
    .nullable()
    .default(null),
  dueMin: z.number().int().min(0).max(1439).nullable().default(null),
});

export const taskStateSchema = z.object({
  state: z.enum(TASK_STATES),
});

export const chatMessageSchema = z.object({
  message: z.string().trim().min(1, 'Skriv en fråga').max(2000),
});

/**
 * Inloggningslänk för en elev utan Google-konto. Se server/src/db/schema.ts
 * för varför den finns.
 */
export interface StudentLoginLink {
  studentUserId: string;
  studentName: string;
  hasLink: boolean;
  /** unix-sekunder */
  createdAt: number | null;
  lastUsedAt: number | null;
}

export interface LoginLinkListView {
  classId: string;
  students: StudentLoginLink[];
}

export const loginLinkSchema = z.object({
  studentUserId: z.string().min(1),
  classId: z.string().nullish(),
});

export const copyWeekSchema = z.object({
  fromWeek: z.string().regex(/^\d{4}-W\d{2}$/),
  toWeek: z.string().regex(/^\d{4}-W\d{2}$/),
  classId: z.string().min(1),
  /** Utan den här lämnas lektioner som redan har innehåll orörda. */
  overwrite: z.boolean().default(false),
});

export interface CopyWeekResult {
  /** Lektioner som fick innehåll. */
  copied: number;
  /** Hade redan innehåll och lämnades orörda. */
  skipped: number;
  /** Fanns i källveckan men har ingen motsvarighet i målveckan. */
  missing: number;
}

/* ------------------------------------------------------------------ */
/* Hjälpare för datum                                                  */
/* ------------------------------------------------------------------ */

/** "2026-09-22" ur ett Date, i lokal tid — aldrig toISOString(). */
export function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Måndag = 1 … söndag = 7 */
export function isoWeekday(d: Date): number {
  return d.getDay() === 0 ? 7 : d.getDay();
}

/** "2026-W39" */
export function toWeekKey(d: Date): string {
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  target.setDate(target.getDate() + 4 - isoWeekday(target));
  const yearStart = new Date(target.getFullYear(), 0, 1);
  const week = Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${target.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

/** Måndagen i veckan som nyckeln pekar ut. */
export function weekKeyToMonday(weekKey: string): Date {
  const [yearPart, weekPart] = weekKey.split('-W');
  const year = Number(yearPart);
  const week = Number(weekPart);
  const jan4 = new Date(year, 0, 4);
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - (isoWeekday(jan4) - 1) + (week - 1) * 7);
  return monday;
}

export function addDays(d: Date, days: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export const WEEKDAY_NAMES = [
  'måndag',
  'tisdag',
  'onsdag',
  'torsdag',
  'fredag',
  'lördag',
  'söndag',
] as const;

export const MONTH_NAMES = [
  'januari',
  'februari',
  'mars',
  'april',
  'maj',
  'juni',
  'juli',
  'augusti',
  'september',
  'oktober',
  'november',
  'december',
] as const;

/** "tisdag 22 september" */
export function formatLongDate(dateKey: string): string {
  const d = parseDateKey(dateKey);
  const weekday = WEEKDAY_NAMES[isoWeekday(d) - 1];
  const month = MONTH_NAMES[d.getMonth()];
  return `${weekday} ${d.getDate()} ${month}`;
}

/* ------------------------------------------------------------------ */
/* Synlighet: visibleFrom                                              */
/* ------------------------------------------------------------------ */

/**
 * visibleFrom är det enda stället i appen där ett klockslag lagras som ISO
 * i stället för minuter från midnatt. Skälet är att det pekar på en punkt i
 * tiden och inte på en plats i en skoldag — "dagen innan kl. 17" ligger på
 * ett annat datum än lektionen. Offseten skrivs alltid ut: aldrig 'Z', aldrig
 * en UTC-konvertering. Se docs/SPEC.md § Tidsmodell.
 */
export function toOffsetIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const offsetMin = -d.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

/** visibleFrom för ett datum ('YYYY-MM-DD') och minuter från midnatt. */
export function offsetIsoAt(dateKey: string, minutes: number): string {
  const d = parseDateKey(dateKey);
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return toOffsetIso(d);
}

/** Värdet ett <input type="datetime-local"> vill ha: "2026-09-21T17:00". */
export function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Tillbaka från <input type="datetime-local">. Tomt fält ger null. */
export function fromDatetimeLocalValue(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return toOffsetIso(d);
}

/** "måndag 21 september kl. 17:00" */
export function formatVisibleFrom(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${formatLongDate(toDateKey(d))} kl. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
